import React, { useState, useMemo, useEffect } from 'react';
import CognitoAuthService from './services/CognitoAuthService';
import ConfirmUser from './components/ConfirmUser';
import { API_BASE_URL } from './config';
import { loadStripe } from '@stripe/stripe-js';

// Cal.com-style appointment calendar: month grid + time slots from Cal.com API
function AppointmentCalendar({ appointmentDate, appointmentTime, onSelect, disabled }) {
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    if (!(appointmentDate || '').trim()) {
      setAvailableSlots([]);
      return;
    }
    setLoadingSlots(true);
    fetch(`${API_BASE_URL}/api/available-slots?date=${encodeURIComponent(appointmentDate)}`)
      .then(res => res.json())
      .then(data => {
        const slots = Array.isArray(data.slots) ? data.slots : [];
        setAvailableSlots(slots);
        if (appointmentTime && slots.length > 0 && !slots.includes(appointmentTime)) {
          onSelect(appointmentDate, '');
        }
      })
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [appointmentDate]);

  const calendarGrid = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startPad = first.getDay();
    const daysInMonth = last.getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push({ day: null, dateStr: null, isToday: false, isPast: false });
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      date.setHours(0, 0, 0, 0);
      cells.push({
        day: d,
        dateStr,
        isToday: date.getTime() === today.getTime(),
        isPast: date.getTime() < today.getTime(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      });
    }
    const total = cells.length;
    const remainder = total % 7;
    if (remainder) for (let i = 0; i < 7 - remainder; i++) cells.push({ day: null, dateStr: null, isToday: false, isPast: false });
    return cells;
  }, [calendarMonth]);

  const hasDate = (appointmentDate || '').trim().length > 0;
  const displayMonth = `${monthNames[calendarMonth.getMonth()]} ${calendarMonth.getFullYear()}`;

  return (
    <div className="appointment-calendar" style={{
      width: '100%',
      maxWidth: '560px',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      overflow: 'hidden',
      background: '#fff',
      margin: '0 auto 12px'
    }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <span style={{ fontWeight: '600', fontSize: '15px', color: '#111827' }}>{displayMonth}</span>
        <span style={{ display: 'flex', gap: '4px' }}>
          <button
            type="button"
            onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            disabled={disabled}
            style={{
              padding: '6px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              background: '#f9fafb',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              color: '#374151'
            }}
          >
            ←
          </button>
          <button
            type="button"
            onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            disabled={disabled}
            style={{
              padding: '6px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              background: '#f9fafb',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              color: '#374151'
            }}
          >
            →
          </button>
        </span>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '8px', fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
          {weekDays.map(w => (
            <div key={w} style={{ textAlign: 'center', padding: '4px 0' }}>{w}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', fontSize: '14px' }}>
          {calendarGrid.map((cell, i) => (
            <button
              key={i}
              type="button"
              disabled={disabled || !cell.dateStr || cell.isWeekend}
              onClick={() => cell.dateStr && onSelect(cell.dateStr, appointmentTime || '')}
              style={{
                padding: '8px 4px',
                border: 'none',
                borderRadius: '8px',
                background: !cell.dateStr ? 'transparent' : cell.dateStr === appointmentDate ? '#111827' : cell.isToday ? '#eff6ff' : (cell.isPast || cell.isWeekend) ? '#f3f4f6' : '#f9fafb',
                color: !cell.dateStr ? 'transparent' : cell.dateStr === appointmentDate ? '#fff' : (cell.isPast || cell.isWeekend) ? '#9ca3af' : '#111827',
                cursor: disabled || !cell.dateStr || cell.isWeekend ? 'default' : 'pointer',
                fontWeight: cell.dateStr === appointmentDate || cell.isToday ? '600' : '400'
              }}
            >
              {cell.day != null ? cell.day : ''}
            </button>
          ))}
        </div>
      </div>
      {hasDate && (
        <div style={{ padding: '12px 14px', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Select a time</div>
          {loadingSlots ? (
            <div style={{ fontSize: '13px', color: '#6b7280' }}>Loading available times…</div>
          ) : availableSlots.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {availableSlots.map(t => (
                <button
                  key={t}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(appointmentDate, t)}
                  style={{
                    padding: '8px 14px',
                    border: appointmentTime === t ? '2px solid #111827' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    background: appointmentTime === t ? '#111827' : '#f9fafb',
                    color: appointmentTime === t ? '#fff' : '#374151',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  {(() => {
                    const [h, m] = t.split(':').map(Number);
                    if (h === 0) return `12:${String(m).padStart(2, '0')} AM`;
                    if (h < 12) return `${h}:${String(m).padStart(2, '0')} AM`;
                    if (h === 12) return `12:${String(m).padStart(2, '0')} PM`;
                    return `${h - 12}:${String(m).padStart(2, '0')} PM`;
                  })()}
                </button>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>No slots available for this date. Please pick another.</p>
          )}
          {(appointmentDate || appointmentTime) && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280' }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>Selected appointment</div>
              <div>{appointmentDate && appointmentTime ? `${appointmentDate} at ${appointmentTime}` : appointmentDate || appointmentTime}</div>
              <button
                type="button"
                onClick={() => onSelect('', '')}
                style={{ marginTop: '6px', padding: 0, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SignUp({ isOpen, onClose, onSwitchToLogin, initialBasicDetails = null }) {
  // When Sign Up is clicked, show basic-details popup first (Email, Name, Phone, Chatbot Name; Chatbot Description removed from UI)
  const [showBasicDetailsPopup, setShowBasicDetailsPopup] = useState(true);
  const [basicDetailsData, setBasicDetailsData] = useState({
    email: '',
    name: '',
    phoneNumber: '',
    chatbotName: '',
    chatbotDescription: '',
    appointmentDate: '',
    appointmentTime: ''
  });

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    chatbotName: '',
    chatbotDescription: '',
    appointmentDate: '',
    appointmentTime: ''
  });
  const [showRecommendationForm, setShowRecommendationForm] = useState(false);
  const [recommendationData, setRecommendationData] = useState({
    userName: '',
    email: '',
    phone: ''
  });
  const [recommendationCheckResult, setRecommendationCheckResult] = useState(null);
  const [hideRecommendationForm, setHideRecommendationForm] = useState(false);
  const [recommendedUserId, setRecommendedUserId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredUsername, setRegisteredUsername] = useState('');
  const [paymentSetupLoading, setPaymentSetupLoading] = useState(false);
  const isAppointmentMode = initialBasicDetails?.mode === 'appointment';

  const cognitoService = new CognitoAuthService();

  useEffect(() => {
    if (!isOpen || !initialBasicDetails) return;

    setShowBasicDetailsPopup(true);
    setError('');
    setBasicDetailsData(prev => ({
      ...prev,
      email: initialBasicDetails.email || prev.email,
      name: initialBasicDetails.name || prev.name,
      chatbotName: initialBasicDetails.chatbotName || prev.chatbotName
    }));
  }, [isOpen, initialBasicDetails]);

  // 格式化电话号码
  const formatPhoneNumber = (phone) => {
    // 移除所有非数字字符
    const cleaned = phone.replace(/\D/g, '');
    
    // 如果是澳大利亚号码（以0开头）
    if (cleaned.startsWith('0')) {
      return '+61' + cleaned.substring(1);
    }
    
    // 如果已经是国际格式（以+开头）
    if (cleaned.startsWith('61')) {
      return '+' + cleaned;
    }
    
    // 如果是美国号码（10位数字）
    if (cleaned.length === 10) {
      return '+1' + cleaned;
    }
    
    // 如果已经是正确格式，直接返回
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // 默认添加+61（澳大利亚）
    return '+61' + cleaned;
  };

  // 验证电话号码
  const validatePhoneNumber = (phone) => {
    const formatted = formatPhoneNumber(phone);
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    return phoneRegex.test(formatted);
  };

  const handleBasicDetailsChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const formatted = formatPhoneNumber(value);
      setBasicDetailsData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setBasicDetailsData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Store in signed_up_data when user clicks Continue (step 1) so details are saved as soon as they are entered
  const handleBasicDetailsContinue = async () => {
    if (!basicDetailsData.email || !basicDetailsData.name || !basicDetailsData.phoneNumber || !basicDetailsData.chatbotName) {
      setError('Please fill in all basic details (Email, Name, Phone Number, Chatbot Name).');
      return;
    }
    if (!validatePhoneNumber(basicDetailsData.phoneNumber)) {
      setError('Please enter a valid phone number (e.g., 0434 123 456 or +61434 123 456)');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/signed-up-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_id: basicDetailsData.email.trim(),
          name: basicDetailsData.name.trim(),
          phone_no: basicDetailsData.phoneNumber.trim(),
          chatbot_name: basicDetailsData.chatbotName.trim(),
          appointment_date: (basicDetailsData.appointmentDate || '').trim() || undefined,
          appointment_time: (basicDetailsData.appointmentTime || '').trim() || undefined
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save sign up details');
      }
      setFormData(prev => ({
        ...prev,
        fullName: basicDetailsData.name,
        email: basicDetailsData.email,
        phoneNumber: basicDetailsData.phoneNumber,
        chatbotName: basicDetailsData.chatbotName,
        chatbotDescription: basicDetailsData.chatbotDescription,
        appointmentDate: basicDetailsData.appointmentDate || '',
        appointmentTime: basicDetailsData.appointmentTime || ''
      }));
      setShowBasicDetailsPopup(false);
      alert('Sign up details saved successfully.');
    } catch (err) {
      console.error('Sign up (signed_up_data on Continue) error:', err);
      setError(err.message || 'Failed to save. Check console and backend.');
      alert('Could not save sign up details: ' + (err.message || 'Unknown error') + '. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 特殊处理电话号码
    if (name === 'phoneNumber') {
      // 实时格式化电话号码
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleRecommendationDataChange = (e) => {
    const { name, value } = e.target;
    setRecommendationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const checkRecommendedUser = async () => {
    if (!recommendationData.email && !recommendationData.phone) {
      alert('Please provide either email or phone number for the recommended user.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/check-recommended-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: recommendationData.userName,
          email: recommendationData.email,
          phone: recommendationData.phone
        })
      });

      const result = await response.json();

      if (response.ok) {
        if (result.exists) {
          setRecommendationCheckResult({
            type: 'success',
            message: 'Thank you, now please enter your detail.'
          });
          setHideRecommendationForm(true); // Hide recommendation form if user exists
          setRecommendedUserId(result.userId); // Store the recommended user ID
        } else {
          setRecommendationCheckResult({
            type: 'info',
            message: 'No existing user found with the provided details. They will be notified when they sign up.'
          });
          setHideRecommendationForm(false); // Show recommendation form if user does not exist
          setRecommendedUserId(null); // Clear recommended user ID if not found
        }
      } else {
        setRecommendationCheckResult({
          type: 'error',
          message: result.error || 'Failed to check recommended user.'
        });
      }
    } catch (error) {
      console.error('Error checking recommended user:', error);
      setRecommendationCheckResult({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    }
  };

  // Store sign up popup details in signed_up_data table (email_id, name, phone_no, chatbot_name)
  const handleSubmitSignedUpData = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.fullName || !formData.phoneNumber || !formData.chatbotName) {
      setError('Please fill in all fields (Name, Email, Phone Number, Chatbot Name).');
      return;
    }
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setError('Please enter a valid phone number (e.g., 0434 123 456 or +61434 123 456)');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/signed-up-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_id: formData.email,
          name: formData.fullName,
          phone_no: formData.phoneNumber,
          chatbot_name: formData.chatbotName,
          appointment_date: (formData.appointmentDate || '').trim() || undefined,
          appointment_time: (formData.appointmentTime || '').trim() || undefined
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save sign up details');
      }
      alert('Sign up details saved successfully.');
      onClose();
    } catch (err) {
      console.error('Sign up (signed_up_data) error:', err);
      setError(err.message || 'Failed to save sign up details');
      alert('Sign up failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  /* COMMENTED OUT: existing Sign Up flow (Cognito, sync-cognito-user, payment setup, confirm dialog)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match!');
      return;
    }

    // 验证电话号码
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setError('Please enter a valid phone number (e.g., 0434 123 456 or +61434 123 456)');
      return;
    }

    // Validate recommendation form if shown
    if (showRecommendationForm) {
      if (!recommendationData.email && !recommendationData.phone) {
        setError('Please provide either email or phone number for the recommended user.');
        return;
      }
    }
    
    setLoading(true);
    setError('');
    
    try {
      // 1. 在Cognito中注册用户
      const cognitoResult = await cognitoService.registerUser(
        formData.email,
        formData.password,
        formData.fullName,
        formData.phoneNumber
      );
      
      console.log('Cognito registration successful:', cognitoResult);
      console.log('User confirmed status from registration:', cognitoResult.userConfirmed);
      console.log('Registration timestamp:', new Date().toISOString());
      console.log('Cognito User ID (username):', cognitoResult.cognitoUserId);
      
      // Check if user needs confirmation
      if (cognitoResult.userConfirmed) {
        console.log('User is already confirmed - no need for verification code');
        
        // 2. 同步用户到数据库
        try {
          const syncResponse = await fetch(`${API_BASE_URL}/api/sync-cognito-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              cognitoUserId: cognitoResult.cognitoUserId,
              email: formData.email,
              full_name: formData.fullName,
              phone: formData.phoneNumber,
              recommendedUserId: recommendedUserId // Always pass recommendedUserId (null if no recommendation)
            })
          });
          
          if (syncResponse.ok) {
            const syncData = await syncResponse.json();
            console.log('User synced to database:', syncData);
          } else {
            console.warn('Failed to sync user to database:', await syncResponse.text());
          }
        } catch (syncError) {
          console.warn('Database sync error:', syncError);
        }
        
        // 3. Proceed to payment setup for confirmed users
        await createPaymentSetupSession(formData.email, formData.fullName);
        onClose();
        return;
      }
      
      console.log('User needs confirmation - proceeding to verification step');
      
      // 2. 同步用户到数据库
      try {
        const syncResponse = await fetch(`${API_BASE_URL}/api/sync-cognito-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cognitoUserId: cognitoResult.cognitoUserId,
            email: formData.email,
            full_name: formData.fullName,
            phone: formData.phoneNumber,
            recommendedUserId: recommendedUserId // Always pass recommendedUserId (null if no recommendation)
          })
        });
        
        if (syncResponse.ok) {
          const syncData = await syncResponse.json();
          console.log('User synced to database:', syncData);
        } else {
          console.warn('Failed to sync user to database:', await syncResponse.text());
        }
      } catch (syncError) {
        console.warn('Database sync error:', syncError);
        // 不阻止注册流程，只记录警告
      }
      
      // 3. 显示确认对话框 - 传递正确的 username
      setRegisteredEmail(formData.email);
      setRegisteredUsername(cognitoResult.cognitoUserId); // 保存正确的 username
      setShowConfirmDialog(true);
      console.log('Setting showConfirmDialog to true, email:', formData.email, 'username:', cognitoResult.cognitoUserId);
      // Don't close the modal yet - let the ConfirmUser modal handle it
      
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err.message || 'Sign up failed');
      alert('Sign up failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  */

  const handleClose = () => {
    setShowBasicDetailsPopup(true);
    setBasicDetailsData({ email: '', name: '', phoneNumber: '', chatbotName: '', chatbotDescription: '' });
    setFormData({ 
      fullName: '', 
      email: '', 
      phoneNumber: '',
      password: '', 
      confirmPassword: '',
      chatbotName: '',
      chatbotDescription: ''
    });
    setShowRecommendationForm(false);
    setRecommendationData({ userName: '', email: '', phone: '' });
    setRecommendationCheckResult(null);
    setHideRecommendationForm(false); // Reset hide state
    setRecommendedUserId(null); // Clear recommended user ID
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError('');
    onClose();
  };

  const handleSwitchToLogin = () => {
    handleClose();
    onSwitchToLogin();
  };

  const handleConfirmationSuccess = () => {
    // 用户确认成功后，进行支付方式设置
    createPaymentSetupSession(registeredEmail, formData.fullName);
  };

  // Function to create payment setup session
  const createPaymentSetupSession = async (userEmail, userName) => {
    try {
      setPaymentSetupLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/create-payment-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: userEmail,
          userName: userName
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment setup session');
      }

      const { sessionId } = await response.json();
      
      // Redirect to Stripe Checkout for payment setup
      const stripe = await loadStripe('pk_live_51RMOUfJZKrN6X9pNIE0K4ADsWn0K1t2zovlkrnmKQIcA0bHhNs141ofA1UlX2ctp0OWZvv5tSEVZG7kLKVKKYCuu00ZMM27ciP');
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Error redirecting to payment setup:', error);
        alert('Error setting up payment method. Please try again.');
      }
    } catch (error) {
      console.error('Error creating payment setup session:', error);
      alert('Error setting up payment method. Please try again.');
    } finally {
      setPaymentSetupLoading(false);
    }
  };

  if (!isOpen) return null;

  // Step 1: Basic details popup (Email, Name, Phone Number, Chatbot Name; Chatbot Description removed from UI)
  if (showBasicDetailsPopup) {
    return (
      <>
        <style>
          {`
            .auth-modal::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()} style={{
            maxHeight: '90vh',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <div className="modal-header auth-header">
              <button className="close-button-top" onClick={handleClose}>×</button>
              <h2 className="modal-title auth-title">{isAppointmentMode ? 'Book Appointment' : 'Sign Up'}</h2>
            </div>
            <div className="auth-form" style={{ padding: '0 1rem' }}>
              {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
                  {error}
                </div>
              )}
              {isAppointmentMode && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px 14px',
                  background: '#f5f3ff',
                  border: '1px solid #ddd6fe',
                  borderRadius: '10px',
                  color: '#5b21b6',
                  fontSize: '14px',
                  lineHeight: 1.5
                }}>
                  Complete your details below and use the existing appointment calendar to choose a suitable booking time.
                </div>
              )}
              {isAppointmentMode && (
                <div className="form-group">
                  <div className="input-with-label" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      Book your appointment
                    </label>
                    <AppointmentCalendar
                      appointmentDate={basicDetailsData.appointmentDate}
                      appointmentTime={basicDetailsData.appointmentTime}
                      onSelect={(dateStr, timeStr) => setBasicDetailsData(prev => ({ ...prev, appointmentDate: dateStr, appointmentTime: timeStr }))}
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
              <div className="form-group">
                <div className="input-with-label">
                  <label htmlFor="basic-email">Email Id</label>
                  <input
                    type="email"
                    id="basic-email"
                    name="email"
                    value={basicDetailsData.email}
                    onChange={handleBasicDetailsChange}
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <div className="input-with-label">
                  <label htmlFor="basic-name">Name</label>
                  <input
                    type="text"
                    id="basic-name"
                    name="name"
                    value={basicDetailsData.name}
                    onChange={handleBasicDetailsChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <div className="input-with-label">
                  <label htmlFor="basic-phoneNumber">Phone Number</label>
                  <input
                    type="tel"
                    id="basic-phoneNumber"
                    name="phoneNumber"
                    value={basicDetailsData.phoneNumber}
                    onChange={handleBasicDetailsChange}
                    placeholder="0434 123 456 or +61434 123 456"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <div className="input-with-label">
                  <label htmlFor="basic-chatbotName">Chatbot Name</label>
                  <input
                    type="text"
                    id="basic-chatbotName"
                    name="chatbotName"
                    value={basicDetailsData.chatbotName}
                    onChange={handleBasicDetailsChange}
                    placeholder="Enter your chatbot name"
                    required
                  />
                </div>
              </div>
              {!isAppointmentMode && (
                <div className="form-group">
                  <div className="input-with-label" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      Book an appointment
                    </label>
                    <AppointmentCalendar
                      appointmentDate={basicDetailsData.appointmentDate}
                      appointmentTime={basicDetailsData.appointmentTime}
                      onSelect={(dateStr, timeStr) => setBasicDetailsData(prev => ({ ...prev, appointmentDate: dateStr, appointmentTime: timeStr }))}
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
              {/* Chatbot Description field removed from UI; existing code kept below
              <div className="form-group">
                <div className="input-with-label">
                  <label htmlFor="basic-chatbotDescription">Chatbot Description</label>
                  <textarea
                    id="basic-chatbotDescription"
                    name="chatbotDescription"
                    value={basicDetailsData.chatbotDescription}
                    onChange={handleBasicDetailsChange}
                    placeholder="Describe your chatbot"
                    required
                    rows={3}
                    style={{ width: '100%', resize: 'vertical', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit' }}
                  />
                </div>
              </div>
              */}
              <div className="form-actions">
                <button type="button" className="auth-submit-button" onClick={handleBasicDetailsContinue} disabled={loading}>
                  {loading ? 'Saving...' : 'Continue'}
                </button>
              </div>
              <div className="auth-footer">
                <p>Already have an account? <span className="link-text" onClick={handleSwitchToLogin}>Login</span></p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step 2: Existing sign up form – only basic fields shown; password, phone number and recommendation commented out below
  return (
    <>
      <style>
        {`
          .auth-modal::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()} style={{
          maxHeight: '90vh',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          <div className="modal-header auth-header">
            <button className="close-button-top" onClick={handleClose}>×</button>
            <h2 className="modal-title auth-title">Sign Up</h2>
          </div>
          
          <form onSubmit={handleSubmitSignedUpData} className="auth-form">
            {error && (
              <div className="error-message" style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
                {error}
              </div>
            )}
            
            {/* Basic details: Email Id, Name, Phone Number, Chatbot Name (Chatbot Description removed from UI) */}
            <div className="form-group">
              <div className="input-with-label" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <label htmlFor="fullName">Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="email">Email Id</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="0434 123 456 or +61434 123 456"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="chatbotName">Chatbot Name</label>
                <input
                  type="text"
                  id="chatbotName"
                  name="chatbotName"
                  value={formData.chatbotName}
                  onChange={handleInputChange}
                  placeholder="Enter your chatbot name"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="input-with-label" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>Book an appointment</label>
                <AppointmentCalendar
                  appointmentDate={formData.appointmentDate}
                  appointmentTime={formData.appointmentTime}
                  onSelect={(dateStr, timeStr) => setFormData(prev => ({ ...prev, appointmentDate: dateStr, appointmentTime: timeStr }))}
                  disabled={loading}
                />
              </div>
            </div>
            {/* Chatbot Description field removed from UI; existing code kept below
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="chatbotDescription">Chatbot Description</label>
                <textarea
                  id="chatbotDescription"
                  name="chatbotDescription"
                  value={formData.chatbotDescription}
                  onChange={handleInputChange}
                  placeholder="Describe your chatbot"
                  required
                  rows={3}
                  disabled={loading}
                  style={{ width: '100%', resize: 'vertical', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit' }}
                />
              </div>
            </div>
            */}

            {/* COMMENTED OUT: password and recommendation (hidden with display:none; uncomment and remove wrapper + switch form back to handleSubmit to restore) */}
            <div style={{ display: 'none' }}>
            <div className="form-group">
              <div style={{
                marginBottom: '20px',
                padding: '15px',
                background: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  alignItems: 'flex-start'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '1em',
                    color: '#0c4a6e',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}>
                    <input
                      type="radio"
                      name="recommendation"
                      checked={showRecommendationForm === true}
                      onChange={() => setShowRecommendationForm(true)}
                      style={{
                        marginRight: '10px',
                        transform: 'scale(1.2)'
                      }}
                    />
                    I was recommended by someone
                  </label>
                </div>
              </div>
            </div>

            {/* Recommendation Form Fields */}
            {showRecommendationForm && !hideRecommendationForm && (
              <div style={{
                marginBottom: '20px',
                padding: '15px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{
                  marginBottom: '15px',
                  color: '#374151',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}>
                  Recommended User Details
                </h4>
                
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    <label style={{
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      color: '#374151',
                      minWidth: '80px'
                    }}>
                      Name:
                    </label>
                    <input
                      type="text"
                      name="userName"
                      value={recommendationData.userName}
                      onChange={handleRecommendationDataChange}
                      placeholder="Enter recommended user's name"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    <label style={{
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      color: '#374151',
                      minWidth: '80px'
                    }}>
                      Email:
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={recommendationData.email}
                      onChange={handleRecommendationDataChange}
                      placeholder="Enter recommended user's email"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <label style={{
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      color: '#374151',
                      minWidth: '80px'
                    }}>
                      Phone:
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={recommendationData.phone}
                      onChange={handleRecommendationDataChange}
                      placeholder="Enter recommended user's phone"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <p style={{
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    marginTop: '8px',
                    fontStyle: 'italic'
                  }}>
                    * Either email or phone number is required
                  </p>
                  
                  <button
                    type="button"
                    onClick={checkRecommendedUser}
                    style={{
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginTop: '15px',
                      width: '100%',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            )}
            
            {/* Recommendation Check Result - Always visible when there's a result */}
            {recommendationCheckResult && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                borderRadius: '8px',
                background: recommendationCheckResult.type === 'success' ? '#f0f9ff' : 
                           recommendationCheckResult.type === 'error' ? '#fef2f2' : '#f0f9ff',
                border: recommendationCheckResult.type === 'success' ? '1px solid #bae6fd' :
                        recommendationCheckResult.type === 'error' ? '1px solid #fecaca' : '1px solid #bae6fd',
                color: recommendationCheckResult.type === 'success' ? '#0369a1' :
                       recommendationCheckResult.type === 'error' ? '#dc2626' : '#0369a1',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                {recommendationCheckResult.message}
              </div>
            )}
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="fullNameOld">Full Name</label>
                <input
                  type="text"
                  id="fullNameOld"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="emailOld">Email</label>
                <input
                  type="email"
                  id="emailOld"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="phoneNumber">Phone No.</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="0434 123 456 or +61434 123 456"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="password">Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="confirmPassword">Confirm</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>
            </div>
            </div>
            {/* END COMMENTED OUT: rest of details */}
            
            <div className="form-actions">
              <button type="submit" className="auth-submit-button" disabled={loading}>
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>
            </div>
            <div className="auth-footer">
              <p>Already have an account? <span className="link-text" onClick={handleSwitchToLogin}>Login</span></p>
            </div>
          </form>
        </div>
      </div>
      
      <ConfirmUser 
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        email={registeredEmail}
        username={registeredUsername}
        onConfirmationSuccess={handleConfirmationSuccess}
      />
    </>
  );
}

export default SignUp;